import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiSearch, FiTarget, FiZap, FiTrendingUp, FiShield, FiBarChart, FiClock, FiDatabase } from 'react-icons/fi';
import './RCAAgent.css';

const RCAAgent = () => {
  const features = [
    {
      icon: <FiZap />,
      title: "Instant AI RCA",
      description: "Get instant root cause analysis with AI-powered investigation across your entire dataset."
    },
    {
      icon: <FiTarget />,
      title: "Full Dataset Analysis",
      description: "Analyze complete datasets for faster and more accurate incident resolution."
    },
    {
      icon: <FiSearch />,
      title: "Automated Investigation",
      description: "AI automatically investigates incidents and provides detailed root cause analysis."
    },
    {
      icon: <FiTrendingUp />,
      title: "Pattern Recognition",
      description: "Identify recurring patterns and predict potential issues before they occur."
    },
    {
      icon: <FiShield />,
      title: "Security Analysis",
      description: "Detect security incidents and provide detailed forensic analysis."
    },
    {
      icon: <FiBarChart />,
      title: "Comprehensive Reporting",
      description: "Generate detailed reports with actionable insights and recommendations."
    }
  ];

  const benefits = [
    "Instant root cause analysis with AI",
    "Full dataset analysis for accurate results",
    "Automated incident investigation",
    "Pattern recognition and prediction",
    "Security incident detection",
    "Comprehensive reporting and insights"
  ];

  return (
    <div className="rca-agent-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>RCA-Agent</h1>
            <p className="hero-subtitle">
              Instantly AI RCA with full dataset analysis for faster, more accurate incident resolution
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>AI-Powered Root Cause Analysis</h2>
            <p>Get instant, accurate root cause analysis with comprehensive dataset investigation</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Our RCA-Agent?</h2>
              <p>
                Our AI-powered RCA agent provides instant, accurate root cause analysis 
                by analyzing your complete dataset for faster incident resolution.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiTarget />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>RCA Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiClock />
                    <span>Analysis Time: 0.3s</span>
                  </div>
                  <div className="metric">
                    <FiDatabase />
                    <span>Data Points: 2.5M</span>
                  </div>
                  <div className="metric">
                    <FiTarget />
                    <span>Accuracy: 98.5%</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready for Instant RCA?</h2>
            <p>Experience AI-powered root cause analysis with full dataset investigation.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RCAAgent;
